import React, { useState, useCallback } from "react";
import {
  Box, Paper, Typography, TextField, Button, Stack, Table, TableBody, TableHead,
  TableRow, TableCell, CircularProgress, Divider, Alert, Chip,
} from "@mui/material";
import api from "../services/api";

const qtyFmt = (n) =>
  (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const pctFmt = (n) => `${(Number(n) || 0).toFixed(1)}%`;

// A tiny dependency-free SVG bar chart: actual recovery % per month.
function TrendChart({ data }) {
  if (!data.length) return null;
  const W = Math.max(320, data.length * 56);
  const H = 180, padB = 26, padT = 10, padL = 6;
  const maxPct = Math.max(100, ...data.map((d) => d.actualPct));
  const barW = (W - padL) / data.length * 0.6;
  const gap = (W - padL) / data.length;
  const y = (v) => padT + (H - padT - padB) * (1 - v / maxPct);
  return (
    <Box sx={{ overflowX: "auto" }}>
      <svg width={W} height={H} role="img" aria-label="Monthly recovery trend">
        {/* 50% and 100% guide lines */}
        {[50, 100].map((g) => (
          <g key={g}>
            <line x1={padL} x2={W} y1={y(g)} y2={y(g)} stroke="#e0e0e0" strokeDasharray="3 3" />
            <text x={padL} y={y(g) - 2} fontSize="9" fill="#9e9e9e">{g}%</text>
          </g>
        ))}
        {data.map((d, i) => {
          const x = padL + gap * i + (gap - barW) / 2;
          const h = Math.max(0, H - padB - y(d.actualPct));
          const good = d.actualPct >= 50;
          return (
            <g key={d.month}>
              <rect x={x} y={y(d.actualPct)} width={barW} height={h} rx="2"
                fill={good ? "#2e7d32" : "#c62828"} opacity="0.85" />
              <text x={x + barW / 2} y={y(d.actualPct) - 3} fontSize="9" textAnchor="middle" fill="#555">
                {pctFmt(d.actualPct)}
              </text>
              <text x={x + barW / 2} y={H - 8} fontSize="9" textAnchor="middle" fill="#777">
                {d.month.slice(2)}
              </text>
            </g>
          );
        })}
      </svg>
    </Box>
  );
}

export default function RecoveryAnalytics() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(() => {
    setLoading(true); setError(null);
    api.get("/wireChopping/recovery-report", { params: { fromDate: fromDate || undefined, toDate: toDate || undefined } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || "Failed to build recovery report"))
      .finally(() => setLoading(false));
  }, [fromDate, toDate]);

  const varColor = (v) => (v >= 0 ? "success.main" : v >= -3 ? "warning.main" : "error.main");

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>Wire Chopping — Recovery Analytics</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Actual copper recovery (<strong>copper collected &divide; wire fed in</strong>) trended over time and by
        wire type, against the expected % set on each job. A consistent gap below expected means you're either
        <strong> overpaying at intake</strong> or <strong>losing copper in the chop</strong>. Read-only — nothing is changed.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <TextField type="date" size="small" label="From (optional)" InputLabelProps={{ shrink: true }}
            value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          <TextField type="date" size="small" label="To (optional)" InputLabelProps={{ shrink: true }}
            value={toDate} onChange={(e) => setToDate(e.target.value)} />
          <Button variant="contained" onClick={run} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Run report"}
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && !loading && (
        <>
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 1 }} flexWrap="wrap">
              <Chip label={`${data.totals.jobs} jobs`} />
              <Chip label={`Wire fed in: ${qtyFmt(data.totals.input)} lb`} />
              <Chip label={`Copper out: ${qtyFmt(data.totals.collected)} lb`} />
              <Chip color={data.totals.actualPct >= 50 ? "success" : "error"}
                label={`Overall recovery: ${pctFmt(data.totals.actualPct)}`} />
            </Stack>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Recovery % by month</Typography>
            {data.byMonth.length ? <TrendChart data={data.byMonth} />
              : <Typography variant="body2" color="text.secondary">No dated jobs in range.</Typography>}
          </Paper>

          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>By wire type (worst gap first)</Typography>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Wire</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Jobs</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Wire in (lb)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Copper out (lb)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Expected %</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Actual %</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Variance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.byWire.length === 0 && (
                    <TableRow><TableCell colSpan={7} sx={{ color: "text.secondary" }}>No jobs found.</TableCell></TableRow>
                  )}
                  {data.byWire.map((w, i) => (
                    <TableRow key={`${w.wire}-${i}`}>
                      <TableCell sx={{ py: 0.4 }}>{w.wire}</TableCell>
                      <TableCell align="right" sx={{ py: 0.4, fontVariantNumeric: "tabular-nums" }}>{w.jobs}</TableCell>
                      <TableCell align="right" sx={{ py: 0.4, fontVariantNumeric: "tabular-nums" }}>{qtyFmt(w.input)}</TableCell>
                      <TableCell align="right" sx={{ py: 0.4, fontVariantNumeric: "tabular-nums" }}>{qtyFmt(w.collected)}</TableCell>
                      <TableCell align="right" sx={{ py: 0.4, fontVariantNumeric: "tabular-nums" }}>{pctFmt(w.expectedPct)}</TableCell>
                      <TableCell align="right" sx={{ py: 0.4, fontVariantNumeric: "tabular-nums" }}>{pctFmt(w.actualPct)}</TableCell>
                      <TableCell align="right" sx={{ py: 0.4, fontVariantNumeric: "tabular-nums", fontWeight: 700, color: varColor(w.variance) }}>
                        {w.variance >= 0 ? "+" : ""}{w.variance.toFixed(1)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.secondary" }}>
              Actual % = total copper collected &divide; total wire fed in, weighted across all jobs for that wire.
              Expected % is the weighted average of the target set on each job. A red variance (worse than &minus;3 points)
              is worth investigating.
            </Typography>
          </Paper>
        </>
      )}
    </Box>
  );
}
